import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";
import { confirm } from "@/shared/lib/confirm";
import { Loader } from "@/shared/ui/Loader";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { StepUpTotpDialog } from "@/modules/auth/presentation/ui/StepUpTotpDialog";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { WalletIcon } from "lucide-react";
import { useState } from "react";
import {
  useApproveTopupMutation,
  useRejectTopupMutation,
  useTopupQueueQuery,
  useTopupQueueSummaryQuery,
} from "../api/queries";
import type { TopupQueueRow } from "@/modules/wallet/domain/types";
import { TopupQueueDetailDialog } from "../ui/TopupQueueDetailDialog";
import { TopupQueueFilter } from "../ui/TopupQueueFilter";
import { TopupQueueTable } from "../ui/TopupQueueTable";
import { TopupQueueSummaryCards } from "../ui/TopupQueueSummaryCards";

type TopupQueueSearch = {
  requestedFrom?: string;
  requestedTo?: string;
  search?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED";
  method?: "CASHIER" | "BANK_SLIP";
  sortField?: string;
  sortOrder?: "asc" | "desc";
  offset?: number;
  limit?: number;
};

export function TopupQueuePage() {
  const { user } = useAuthState();
  const nav = useNavigate({ from: "/app/wallet/topup-queue" });
  const [stepUp, setStepUp] = useState<{ open: boolean; topupId: string | null }>({
    open: false,
    topupId: null,
  });
  const [detailRow, setDetailRow] = useState<TopupQueueRow | null>(null);
  const twoFactorEnabled =
    (user as { twoFactorEnabled?: boolean } | null)?.twoFactorEnabled === true;
  const search = useSearch({
    from: "/app/wallet/topup-queue",
  }) as TopupQueueSearch;

  const params = {
    requestedFrom: search.requestedFrom,
    requestedTo: search.requestedTo,
    search: search.search,
    status: search.status,
    method: search.method,
    sortField: (search.sortField ?? "requestedAt") as "requestedAt",
    sortOrder: (search.sortOrder ?? "desc") as "asc" | "desc",
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useTopupQueueQuery(params);
  const { data: summaryData, isLoading: summaryLoading } =
    useTopupQueueSummaryQuery();
  const approveMutation = useApproveTopupMutation();
  const rejectMutation = useRejectTopupMutation();

  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const runApprove = async (id: string, totpCode?: string) => {
    try {
      const res = await approveMutation.mutateAsync({ id, totpCode });
      if ("ok" in res && res.ok) {
        toast.success("ອະນຸມັດແລ້ວ");
      } else {
        toast.error("ຜິດພາດ", {
          description: (res as { error?: string }).error ?? "ALREADY_PROCESSED",
        });
      }
    } catch {
      // fetcher already toasts
    }
  };

  const handleApprove = async (id: string) => {
    const ok = await confirm({
      title: "ອະນຸມັດເຕີມເງິນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການອະນຸມັດການເຕີມເງິນນີ້?",
      actionText: "ອະນຸມັດ",
      cancelText: "ຍົກເລີກ",
    });
    if (!ok) return;
    if (twoFactorEnabled) {
      setStepUp({ open: true, topupId: id });
      return;
    }
    await runApprove(id);
  };

  const handleReject = async (id: string) => {
    const ok = await confirm({
      title: "ປະຕິເສດເຕີມເງິນ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການປະຕິເສດການເຕີມເງິນນີ້?",
      actionText: "ປະຕິເສດ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      const res = await rejectMutation.mutateAsync(id);
      if ("ok" in res && res.ok) {
        toast.success("ປະຕິເສດແລ້ວ");
      } else {
        toast.error("ຜິດພາດ", {
          description: (res as { error?: string }).error ?? "ALREADY_PROCESSED",
        });
      }
    } catch {
      // fetcher already toasts
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຄິວອະນຸມັດເຕີມເງິນ",
              to: "/app/wallet/topup-queue",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <WalletIcon className="size-5" />
              ຄິວອະນຸມັດເຕີມເງິນ
            </h2>
            <p className="text-muted-foreground">
              ສລິບໂອນລໍຖ້າອະນຸມັດ; ເຕີມທີ່ເຄົ້າເຕີ POS (QR) ສະແດງເປັນອະນຸມັດແລ້ວ ພ້ອມຮູບຫຼັກຖານ.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <TopupQueueSummaryCards
            queueHealth={summaryData?.queueHealth ?? 0}
            todaysApprovals={summaryData?.todaysApprovals ?? 0}
            totalProcessed={summaryData?.totalProcessed ?? 0}
            isLoading={summaryLoading}
          />
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <TopupQueueFilter />

          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <TopupQueueTable
              data={items}
              isLoading={isLoading}
              offset={params.offset}
              limit={params.limit}
              totalCount={totalCount}
              sortBy={params.sortField}
              sortOrder={params.sortOrder}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sortField: id,
                    sortOrder: desc ? "desc" : "asc",
                  },
                })
              }
              onPaginationChange={(newOffset, newLimit) =>
                nav({
                  search: {
                    ...search,
                    offset: newOffset,
                    limit: newLimit,
                  },
                })
              }
              onApprove={handleApprove}
              onReject={handleReject}
              onViewDetail={(row) => setDetailRow(row)}
              isApproving={
                approveMutation.isPending
                  ? approveMutation.variables?.id ?? null
                  : null
              }
              isRejecting={rejectMutation.isPending ? rejectMutation.variables : null}
            />
          )}
        </div>
      </Main>
      <TopupQueueDetailDialog
        row={detailRow}
        open={detailRow != null}
        onOpenChange={(open) => {
          if (!open) setDetailRow(null);
        }}
      />
      <StepUpTotpDialog
        open={stepUp.open}
        loading={approveMutation.isPending}
        onClose={() => setStepUp({ open: false, topupId: null })}
        onConfirm={async (totpCode) => {
          if (!stepUp.topupId) return;
          await runApprove(stepUp.topupId, totpCode);
          setStepUp({ open: false, topupId: null });
        }}
      />
    </>
  );
}
